import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Property } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Save, X } from "lucide-react";
import { useLanguage } from "@/lib/language";
import { extractImagePaths } from "@/lib/api";

interface PropertyFormProps {
  property: Property | null;
  onSave: (
    property: Partial<Property>,
    imageFiles?: File[],
    existingImagePaths?: string[],
    videoFile?: File,
    keepExistingVideo?: boolean
  ) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

interface FormData {
  free_text_en: string;
  free_text_he: string;
  free_text_fr: string;
  city_en: string;
  city_he: string;
  city_fr: string;
  neighborhood_en: string;
  neighborhood_he: string;
  neighborhood_fr: string;
  price: number;
  rooms: number;
  bedrooms: number;
  bathrooms: number;
  size: number;
  action: "rent" | "sell";
  status: boolean;
}

export function PropertyForm({
  property,
  onSave,
  onCancel,
  isLoading,
}: PropertyFormProps) {
  const [existingImages, setExistingImages] = useState<string[]>(
    property?.images || []
  );
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
  const [video, setVideo] = useState<string | undefined>(property?.video);
  const [videoFile, setVideoFile] = useState<File | undefined>(undefined);
  const [keepExistingVideo, setKeepExistingVideo] = useState<boolean>(true);
  const { t, dir } = useLanguage();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      free_text_en: property?.description.en || "",
      free_text_he: property?.description.he || "",
      free_text_fr: property?.description.fr || "",
      city_en: property?.location?.city?.en || "",
      city_he: property?.location?.city?.he || "",
      city_fr: property?.location?.city?.fr || "",
      neighborhood_en: property?.location?.neighborhood?.en || "",
      neighborhood_he: property?.location?.neighborhood?.he || "",
      neighborhood_fr: property?.location?.neighborhood?.fr || "",
      price: property?.price || 0,
      rooms: property?.rooms || 1,
      bedrooms: property?.bedrooms || property?.rooms || 1,
      bathrooms: property?.bathrooms || 1,
      size: property?.size || 50,
      action: property?.type === "sale" ? "sell" : "rent",
      status: property?.status === "available",
    },
  });

  useEffect(() => {
    if (property) {
      reset({
        free_text_en: property.description.en,
        free_text_he: property.description.he,
        free_text_fr: property.description.fr,
        city_en: property.location?.city?.en || "",
        city_he: property.location?.city?.he || "",
        city_fr: property.location?.city?.fr || "",
        neighborhood_en: property.location?.neighborhood?.en || "",
        neighborhood_he: property.location?.neighborhood?.he || "",
        neighborhood_fr: property.location?.neighborhood?.fr || "",
        price: property.price,
        rooms: property.rooms,
        bedrooms: property.bedrooms || property.rooms,
        bathrooms: property.bathrooms || 1,
        size: property.size,
        action: property.type === "sale" ? "sell" : "rent",
        status: property.status === "available",
      });
      setExistingImages(property.images);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setVideo(property.video);
      setVideoFile(undefined);
      setKeepExistingVideo(true);
    }
  }, [property, reset]);

  useEffect(() => {
    return () => {
      newImagePreviews.forEach((img) => {
        if (img.startsWith("blob:")) URL.revokeObjectURL(img);
      });
      if (video && video.startsWith("blob:")) URL.revokeObjectURL(video);
    };
  }, [newImagePreviews, video]);

  const onSubmit = async (data: FormData) => {
    const existingImagePaths = extractImagePaths(existingImages);

    const propertyData: Partial<Property> = {
      title: {
        en: data.free_text_en,
        he: data.free_text_he,
        fr: data.free_text_fr,
      },
      description: {
        en: data.free_text_en,
        he: data.free_text_he,
        fr: data.free_text_fr,
      },
      location: {
        city: {
          en: data.city_en,
          he: data.city_he,
          fr: data.city_fr,
        },
        neighborhood: {
          en: data.neighborhood_en,
          he: data.neighborhood_he,
          fr: data.neighborhood_fr,
        },
        address: {
          en: `${data.neighborhood_en}, ${data.city_en}, Israel`,
          he: `${data.neighborhood_he}, ${data.city_he}, ישראל`,
          fr: `${data.neighborhood_fr}, ${data.city_fr}, Israël`,
        },
      },
      price: data.price,
      currency: "ILS",
      rooms: data.rooms,
      bedrooms: data.bedrooms,
      bathrooms: data.bathrooms,
      size: data.size,
      type: data.action === "sell" ? "sale" : "rent",
      status: data.status ? "available" : "sold",
      features: [],
    };

    await onSave(
      propertyData,
      newImageFiles,
      existingImagePaths,
      videoFile,
      keepExistingVideo
    );
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      const newImageUrls = newFiles.map((file) => URL.createObjectURL(file));
      setNewImageFiles((prev) => [...prev, ...newFiles]);
      setNewImagePreviews((prev) => [...prev, ...newImageUrls]);
    }
  };

  const allImages = [...existingImages, ...newImagePreviews];

  const removeImage = (index: number) => {
    const isExistingImage = index < existingImages.length;
    if (isExistingImage) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newImageIndex = index - existingImages.length;
      const imageToRemove = newImagePreviews[newImageIndex];
      if (imageToRemove && imageToRemove.startsWith("blob:")) {
        URL.revokeObjectURL(imageToRemove);
      }
      setNewImageFiles((prev) => prev.filter((_, i) => i !== newImageIndex));
      setNewImagePreviews((prev) => prev.filter((_, i) => i !== newImageIndex));
    }
  };

  const clearAllImages = () => {
    setExistingImages([]);
    newImagePreviews.forEach((url) => {
      if (url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
    setNewImageFiles([]);
    setNewImagePreviews([]);
  };

  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (video && video.startsWith("blob:")) URL.revokeObjectURL(video);
      const videoUrl = URL.createObjectURL(file);
      setVideoFile(file);
      setVideo(videoUrl);
      setKeepExistingVideo(false);
    }
  };

  const removeVideo = () => {
    if (video && video.startsWith("blob:")) URL.revokeObjectURL(video);
    setVideo(undefined);
    setVideoFile(undefined);
    setKeepExistingVideo(false);
  };

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col items-start">
          <Button variant="ghost" size="sm" onClick={onCancel} className="mb-2">
            {dir === "rtl" ? (
              <ArrowRight className="h-4 w-4" />
            ) : (
              <ArrowLeft className="h-4 w-4" />
            )}
            {t("backToProperties")}
          </Button>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {property ? t("editProperty") : t("addNewProperty")}
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              {property
                ? t("updatePropertyDetails")
                : t("createNewPropertyListing")}
            </p>
          </div>
        </div>
        {property && (
          <Badge variant="outline" className="self-start">
            ID: {property.id}
          </Badge>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Tabs defaultValue="basic" className="w-full" dir={dir}>
          <TabsList className="grid w-full grid-cols-3 h-auto">
            <TabsTrigger
              value="basic"
              className="text-xs sm:text-sm p-2 sm:p-3"
            >
              {t("basicInformation")}
            </TabsTrigger>
            <TabsTrigger
              value="details"
              className="text-xs sm:text-sm p-2 sm:p-3"
            >
              {t("propertyDetails")}
            </TabsTrigger>
            <TabsTrigger
              value="images"
              className="text-xs sm:text-sm p-2 sm:p-3"
            >
              {t("mediaGallery")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("multiLanguageContent")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="free_text_en">
                      {t("descriptionInEnglish")}
                    </Label>
                    <Textarea
                      id="free_text_en"
                      {...register("free_text_en", {
                        required: "English description is required",
                      })}
                      placeholder="Beautiful property with..."
                      rows={4}
                      dir={dir}
                    />
                    {errors.free_text_en && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.free_text_en.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="free_text_he">
                      {t("descriptionInHebrew")}
                    </Label>
                    <Textarea
                      id="free_text_he"
                      {...register("free_text_he")}
                      placeholder="נכס יפה עם..."
                      rows={4}
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="free_text_fr">
                      {t("descriptionInFrench")}
                    </Label>
                    <Textarea
                      id="free_text_fr"
                      {...register("free_text_fr")}
                      placeholder="Belle propriété avec..."
                      rows={4}
                      dir={dir}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city_en">{t("cityInEnglish")}</Label>
                    <Input
                      id="city_en"
                      {...register("city_en")}
                      placeholder="Netanya"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city_he">{t("cityInHebrew")}</Label>
                    <Input
                      id="city_he"
                      {...register("city_he")}
                      placeholder="נתניה"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city_fr">{t("cityInFrench")}</Label>
                    <Input
                      id="city_fr"
                      {...register("city_fr")}
                      placeholder="Netanya"
                      dir={dir}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="neighborhood_en">
                      {t("neighborhoodInEnglish")}
                    </Label>
                    <Input
                      id="neighborhood_en"
                      {...register("neighborhood_en")}
                      placeholder="Center"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="neighborhood_he">
                      {t("neighborhoodInHebrew")}
                    </Label>
                    <Input
                      id="neighborhood_he"
                      {...register("neighborhood_he")}
                      placeholder="מרכז"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="neighborhood_fr">
                      {t("neighborhoodInFrench")}
                    </Label>
                    <Input
                      id="neighborhood_fr"
                      {...register("neighborhood_fr")}
                      placeholder="Centre"
                      dir={dir}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="details" className="space-y-4" dir={dir}>
            <Card>
              <CardHeader>
                <CardTitle>{t("propertyDetails")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div>
                    <Label htmlFor="price">{t("price")}</Label>
                    <Input
                      id="price"
                      type="number"
                      {...register("price", {
                        required: "Price is required",
                        valueAsNumber: true,
                      })}
                      placeholder="12000"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="rooms">{t("rooms")}</Label>
                    <Input
                      id="rooms"
                      type="number"
                      {...register("rooms", {
                        required: "Rooms is required",
                        valueAsNumber: true,
                      })}
                      placeholder="3"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="size">{t("sizeSquareMeters")}</Label>
                    <Input
                      id="size"
                      type="number"
                      {...register("size", {
                        required: "Size is required",
                        valueAsNumber: true,
                      })}
                      placeholder="85"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bedrooms">Bedrooms</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      {...register("bedrooms", {
                        required: "Bedrooms is required",
                        valueAsNumber: true,
                      })}
                      placeholder="3"
                      dir={dir}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bathrooms">Bathrooms</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      {...register("bathrooms", {
                        required: "Bathrooms is required",
                        valueAsNumber: true,
                      })}
                      placeholder="2"
                      dir={dir}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="action">{t("propertyType")}</Label>
                    <Select
                      value={watch("action")}
                      onValueChange={(value) =>
                        setValue("action", value as any)
                      }
                      dir={dir}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rent">{t("forRent")}</SelectItem>
                        <SelectItem value="sell">{t("forSale")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="status">{t("status")}</Label>
                    <Select
                      value={watch("status") ? "available" : "sold"}
                      onValueChange={(value) =>
                        setValue("status", value === "available")
                      }
                      dir={dir}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">
                          {t("available")}
                        </SelectItem>
                        <SelectItem value="sold">{t("sold")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="images" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("propertyImages")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="imageUpload">{t("uploadImages")}</Label>
                  <Input
                    id="imageUpload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="cursor-pointer"
                    dir={dir}
                  />
                </div>

                {allImages.length > 0 && (
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted-foreground">
                      {allImages.length} image(s) selected (
                      {existingImages.length} existing, {newImageFiles.length}{" "}
                      new)
                    </p>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={clearAllImages}
                    >
                      Clear All Images
                    </Button>
                  </div>
                )}

                {allImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {allImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Property image ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeImage(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        {index < existingImages.length && (
                          <div className="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                            Existing
                          </div>
                        )}
                        {index >= existingImages.length && (
                          <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                            New
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("propertyVideo")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="videoUpload">{t("uploadVideo")}</Label>
                  <Input
                    id="videoUpload"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="cursor-pointer"
                    dir={dir}
                  />
                </div>

                {video && (
                  <div className="relative">
                    <video
                      src={video}
                      controls
                      className="w-full max-h-64 rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={removeVideo}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex flex-col sm:flex-row items-center justify-end space-y-2 sm:space-y-0 sm:space-x-4 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {isLoading ? (
              <>{t("saving")}</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {property ? t("updateProperty") : t("createProperty")}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
